import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Info } from "lucide-react";
import { InvestorStrategyRecord } from "../../../services/investorStrategyService";
import { ProductLookupEntry, ProductLookupKey, getStrategyDisplayName } from "../../../utils/strategyUtils";
import { formatWeight } from "../../../utils/numberUtils";

export const PIE_COLORS = [
  "#14b8a6", "#f59e0b", "#a855f7", "#f43f5e", "#10b981",
  "#3b82f6", "#ec4899", "#8b5cf6", "#06b6d4", "#84cc16",
  "#ef4444", "#6366f1", "#d946ef", "#0ea5e9", "#eab308",
  "#f97316", "#22c55e", "#e879f9", "#2dd4bf", "#fb923c",
];

const DONUT_COLLAPSED_HEIGHT = 480;

function StrategyDonutChart({
  strategies,
  productLookup,
  totalWeight,
  useDrifted,
  capacityPct,
  appearance,
}: {
  strategies: InvestorStrategyRecord[];
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  totalWeight: number;
  useDrifted: boolean;
  capacityPct: number;
  appearance: "default" | "alpha";
}) {
  const size = 200;
  const strokeWidth = 32;
  const radius = (size - strokeWidth) / 2;
  const center = size / 2;
  const circumference = 2 * Math.PI * radius;

  const displayTotal = useDrifted
    ? strategies.reduce((sum, s) => sum + (s.driftedWeight ?? 0), 0)
    : totalWeight;
  const effectiveTotal = Math.max(displayTotal, capacityPct);
  let cumulative = 0;
  const slices = strategies.map((entry, idx) => {
    const weight = useDrifted ? (entry.driftedWeight ?? 0) : parseFloat(entry.weight);
    const fraction = effectiveTotal > 0 ? weight / effectiveTotal : 0;
    const offset = cumulative;
    cumulative += fraction;
    return {
      entry,
      name: getStrategyDisplayName(entry, productLookup),
      weight,
      fraction,
      offset,
      color: PIE_COLORS[idx % PIE_COLORS.length],
    };
  });

  const cashPct = Math.max(0, 100 - displayTotal);
  const marginFreePct = Math.max(0, capacityPct - Math.max(displayTotal, 100));

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="mb-4">
        {/* Background ring */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="currentColor"
          className={appearance === "alpha" ? "text-v7-mist" : "text-gray-200 dark:text-gray-800"}
          strokeWidth={strokeWidth}
        />
        {/* Slices */}
        {slices.map((slice) => (
          <circle
            key={`${slice.entry.entityType}-${slice.entry.productId}`}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            stroke={slice.color}
            strokeWidth={strokeWidth}
            strokeDasharray={`${slice.fraction * circumference} ${circumference}`}
            strokeDashoffset={-slice.offset * circumference}
            transform={`rotate(-90 ${center} ${center})`}
            className="transition-all duration-500"
          />
        ))}
        {/* Center label */}
        <text
          x={center}
          y={center - 8}
          textAnchor="middle"
          className={appearance === "alpha" ? "fill-v7-ink font-v7-serif text-2xl font-medium" : "fill-gray-900 text-2xl font-bold dark:fill-white"}
          style={{ fontSize: "28px", fontWeight: 700 }}
        >
          {formatWeight(displayTotal)}%
        </text>
        <text
          x={center}
          y={center + 14}
          textAnchor="middle"
          className={appearance === "alpha" ? "fill-v7-ink-soft" : "fill-gray-600 dark:fill-white"}
          style={{ fontSize: "12px" }}
        >
          {useDrifted ? "current" : "allocated"}
        </text>
      </svg>

      {/* Legend */}
      <div className="w-full space-y-2 max-h-[260px] overflow-y-auto">
        {slices.map((slice) => (
          <div
            key={`${slice.entry.entityType}-${slice.entry.productId}`}
            className="flex items-center justify-between gap-2"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span
                className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                style={{ backgroundColor: slice.color }}
              />
              <span className={`truncate text-sm ${appearance === "alpha" ? "text-v7-ink" : "text-gray-700 dark:text-gray-300"}`}>
                {slice.name}
              </span>
            </div>
            <span className={`flex-shrink-0 font-mono text-sm ${appearance === "alpha" ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-300"}`}>
              {formatWeight(slice.weight)}%
            </span>
          </div>
        ))}
        {cashPct > 0 && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${appearance === "alpha" ? "bg-v7-mist" : "bg-gray-300 dark:bg-gray-700"}`} />
              <span className={`text-sm ${appearance === "alpha" ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-400"}`}>Cash</span>
            </div>
            <span className={`flex-shrink-0 font-mono text-sm ${appearance === "alpha" ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-400"}`}>
              {formatWeight(cashPct)}%
            </span>
          </div>
        )}
        {marginFreePct > 0 && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${appearance === "alpha" ? "bg-v7-ink/15" : "bg-gray-200 dark:bg-gray-600"}`} />
              <span className={`text-sm ${appearance === "alpha" ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-400"}`}>Available margin</span>
            </div>
            <span className={`flex-shrink-0 font-mono text-sm ${appearance === "alpha" ? "text-v7-ink-soft" : "text-gray-600 dark:text-gray-400"}`}>
              {formatWeight(marginFreePct)}%
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function StrategyAllocationTooltip({ appearance }: { appearance: "default" | "alpha" }) {
  const isAlpha = appearance === "alpha";
  const iconRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  const open = useCallback(() => {
    if (iconRef.current) {
      const rect = iconRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, left: rect.left });
    }
    setShow(true);
  }, []);

  return (
    <div
      ref={iconRef}
      onMouseEnter={open}
      onMouseLeave={() => setShow(false)}
    >
      <Info className={`h-3 w-3 cursor-help transition-colors ${isAlpha ? "text-v7-ink/40 hover:text-v7-bronze-dark" : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"}`} />
      {show && createPortal(
        <div
          style={{ position: "fixed", top: pos.top, left: pos.left }}
          data-testid="strategy-allocation-tooltip"
          data-appearance={appearance}
          className={isAlpha
            ? "z-50 w-72 rounded-v7-popover border border-v7-ink/15 bg-v7-white px-4 py-3 font-v7-sans text-xs leading-relaxed text-v7-ink-soft shadow-lg"
            : "z-50 w-72 rounded-lg border border-gray-700 bg-gray-900 px-4 py-3 text-xs leading-relaxed text-gray-300 shadow-lg"}
        >
          When live allocation data is available, this chart shows your current portfolio weights; otherwise it shows your target strategy allocation. The "Available" number is your unused gross capacity (your cap is 100% plus any margin you have enabled). Plutus automatically triggers trades to realign your account as changes are made.
        </div>,
        document.body,
      )}
    </div>
  );
}

export default function CollapsibleDonutCard({
  strategies,
  productLookup,
  totalWeight,
  capacityPct = 100,
  appearance = "default",
}: {
  strategies: InvestorStrategyRecord[];
  productLookup: Map<ProductLookupKey, ProductLookupEntry>;
  totalWeight: number;
  capacityPct?: number;
  appearance?: "default" | "alpha";
}) {
  const isAlpha = appearance === "alpha";
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [needsExpand, setNeedsExpand] = useState(false);

  const useDrifted = strategies.length > 0 && strategies.every(s => s.driftedWeight != null);
  const displayTotal = useDrifted
    ? strategies.reduce((sum, s) => sum + (s.driftedWeight ?? 0), 0)
    : totalWeight;
  const headerCashPct = Math.max(0, 100 - displayTotal);
  const headerMarginFreePct = Math.max(0, capacityPct - Math.max(displayTotal, 100));
  const overPct = Math.max(0, displayTotal - capacityPct);
  const isOver = overPct > 0;
  const hasMargin = capacityPct > 100;

  useEffect(() => {
    if (contentRef.current) {
      setNeedsExpand(contentRef.current.scrollHeight > DONUT_COLLAPSED_HEIGHT);
    }
  }, [strategies, totalWeight]);

  return (
    <div className={`overflow-hidden border ${isAlpha
      ? "rounded-v7-panel border-v7-ink/15 bg-v7-white"
      : "rounded-lg border-gray-200 bg-white dark:border-rp-separator dark:bg-rp-surface"}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 px-6 pt-5 sm:pt-4">
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold uppercase tracking-wider sm:text-[11px] sm:font-semibold ${isAlpha ? "text-v7-ink" : "text-gray-900 dark:text-white"}`}>
            {useDrifted ? "Current Allocation" : "Strategy Allocation"}
          </span>
          <StrategyAllocationTooltip appearance={appearance} />
        </div>
        <div className={`flex min-w-0 flex-wrap items-center justify-start gap-x-2 gap-y-1 font-mono text-sm sm:justify-end sm:text-xs ${isAlpha ? "text-v7-ink-soft" : "text-gray-700 dark:text-gray-200"}`}>
          <span className="whitespace-nowrap">{formatWeight(headerCashPct)}% Cash</span>
          {hasMargin && !isOver && (
            <span className="whitespace-nowrap">
              <span className={`hidden sm:inline ${isAlpha ? "text-v7-ink/35" : "text-gray-400 dark:text-gray-500"}`}>· </span>{formatWeight(headerMarginFreePct)}% Margin free <span className={isAlpha ? "text-v7-ink/60" : "text-gray-500 dark:text-gray-400"}>(cap {formatWeight(capacityPct)}%)</span>
            </span>
          )}
          {isOver && (
            <span className={`inline-flex whitespace-nowrap items-center gap-1 ${isAlpha ? "text-v7-rust" : "text-red-600 dark:text-red-500"}`}>
              <span className={`hidden sm:inline ${isAlpha ? "text-v7-ink/35" : "text-gray-400 dark:text-gray-500"}`}>·</span>
              <span>Over {formatWeight(overPct)}%</span>
              <span className={`border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${isAlpha
                ? "rounded-v7-control border-v7-rust/30 bg-v7-rust/10 text-v7-rust"
                : "rounded border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"}`}>
                Over
              </span>
            </span>
          )}
        </div>
      </div>
      <div
        ref={contentRef}
        className="p-6 pt-2 transition-[max-height] duration-300 ease-in-out overflow-hidden"
        style={{ maxHeight: expanded ? contentRef.current?.scrollHeight ?? "none" : DONUT_COLLAPSED_HEIGHT }}
      >
        <StrategyDonutChart
          strategies={strategies}
          productLookup={productLookup}
          totalWeight={totalWeight}
          useDrifted={useDrifted}
          capacityPct={capacityPct}
          appearance={appearance}
        />
      </div>
      {needsExpand && (
        <button
          onClick={() => setExpanded(prev => !prev)}
          className={`flex w-full items-center justify-center gap-1 border-t py-2 transition-colors ${isAlpha
            ? "border-v7-ink/15 text-v7-ink/50 hover:bg-v7-paper hover:text-v7-ink"
            : "border-gray-200 text-gray-400 hover:text-gray-600 dark:border-rp-separator dark:hover:text-gray-300"}`}
        >
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-300 ${expanded ? "rotate-180" : ""}`}
          />
          <span className="text-[10px] uppercase tracking-wider font-medium">
            {expanded ? "Show less" : "Show more"}
          </span>
        </button>
      )}
    </div>
  );
}
